/**
 * Output + Segments display block.
 *
 * Spec: v2-design/CARD_SHELL_v0_spec.md §5.3, §10.
 *
 * Renders the Output line (unified Output shape: selected + candidates)
 * with the Segments list indented beneath. Display-only — segments are
 * agent-owned (memory: project_v2_card_edit_scope). The Output line
 * accepts an optional `outputAction` slot for parent-owned edit
 * affordances; this component itself stays presentational.
 *
 * Rationale rendering:
 *   - `selected.selection_rationale` (when non-null) → small info (i)
 *     icon next to the Output line; click opens a Popover with the
 *     rationale text. Shown whenever interpretation was involved,
 *     regardless of whether candidates exist.
 *   - `segment_rationale` → collapsed "Why this segmentation?" disclosure
 *     beneath the segment rows; expands into a left-bordered, tinted
 *     panel visually distinct from the enumeration.
 *
 * Segment rendering rules:
 *   - One row per dimension present in `segments`; missing dimensions
 *     produce no row.
 *   - Instances within a dimension are comma-separated, capped at
 *     INSTANCE_CAP = 6. Overflow shows "+N more" → click expands inline.
 *   - Empty `instances: []` on a dimension = "(all)" — the executor
 *     materializes the full list at run time.
 */
import React, { useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Link, Stack, Typography } from '@mui/material';

import { SelectionRationalePopover } from './SelectionRationalePopover';

const INSTANCE_CAP = 6;

interface DimensionInstance {
  name: string;
  id: number;
}

interface DimensionSegment {
  dimension_depth: number;
  dimension_name: string;
  instances: DimensionInstance[];
}

interface SelectedOutput {
  name: string;
  id: number;
  selection_rationale?: string | null;
}

interface OutputCandidate {
  name: string;
  id: number;
  similarity?: number | null;
}

interface Output {
  selected: SelectedOutput;
  candidates: OutputCandidate[];
}

interface OutputAndSegmentsBlockProps {
  output: Output;
  segments: DimensionSegment[];
  /** Muted secondary text shown in a collapsed disclosure beneath segments. */
  segmentRationale?: string;
  /**
   * Optional control that REPLACES the read-only output-name display.
   * Param-card bodies pass an inline picker here; presentational
   * consumers (terminal result cards, plan_confirmation) omit it and
   * get the plain text rendering.
   */
  outputControl?: React.ReactNode;
}

export const OutputAndSegmentsBlock: React.FC<OutputAndSegmentsBlockProps> = ({
  output,
  segments,
  segmentRationale,
  outputControl,
}) => {
  const outputName = output.selected.name;
  const selectionRationale = output.selected.selection_rationale ?? null;

  const hasSegmentsContent = segments.length > 0 || Boolean(segmentRationale);

  return (
    <Stack spacing={0.5}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          py: 0.25,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: 'text.secondary',
            width: 120,
            flexShrink: 0,
            lineHeight: 1.5,
          }}
        >
          Output
        </Typography>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {outputControl ? (
            outputControl
          ) : (
            <Typography
              component="span"
              sx={{ fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' }}
            >
              {outputName}
            </Typography>
          )}
          {selectionRationale ? (
            <SelectionRationalePopover
              rationale={selectionRationale}
              ariaLabel="Why this Output was selected"
            />
          ) : null}
        </Box>
      </Box>

      {hasSegmentsContent ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            py: 0.25,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: 'text.secondary',
              width: 120,
              flexShrink: 0,
              lineHeight: 1.5,
            }}
          >
            Segments
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {segments.length > 0 ? (
              <Stack spacing={0.25}>
                {segments.map((seg) => (
                  <SegmentRow
                    key={`${seg.dimension_depth}-${seg.dimension_name}`}
                    segment={seg}
                  />
                ))}
              </Stack>
            ) : null}
            {segmentRationale ? (
              <Box sx={{ mt: segments.length > 0 ? 0.75 : 0 }}>
                <SegmentRationaleDisclosure text={segmentRationale} />
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
};

interface SegmentRationaleDisclosureProps {
  text: string;
}

const SegmentRationaleDisclosure: React.FC<SegmentRationaleDisclosureProps> = ({
  text,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Link
        component="button"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          fontSize: 13,
          color: 'text.secondary',
          textDecoration: 'none',
          '&:hover': { color: 'text.primary' },
        }}
      >
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 18 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 18 }} />
        )}
        Why this segmentation?
      </Link>
      {open ? (
        <Box
          sx={{
            mt: 0.5,
            ml: 0.5,
            pl: 1.25,
            py: 0.75,
            pr: 1,
            borderLeft: '3px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
            borderRadius: '0 4px 4px 0',
          }}
        >
          <Typography
            sx={{ fontSize: 13, lineHeight: 1.6, color: 'text.primary' }}
          >
            {text}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

interface SegmentRowProps {
  segment: DimensionSegment;
}

const SegmentRow: React.FC<SegmentRowProps> = ({ segment }) => {
  const [expanded, setExpanded] = useState(false);
  const { dimension_name, instances } = segment;

  const isAll = instances.length === 0;
  const overflow = instances.length > INSTANCE_CAP;
  const visibleInstances =
    expanded || !overflow ? instances : instances.slice(0, INSTANCE_CAP);
  const hiddenCount = overflow ? instances.length - INSTANCE_CAP : 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Typography
        component="span"
        sx={{
          fontSize: 13,
          color: 'text.secondary',
          minWidth: 96,
          flexShrink: 0,
        }}
      >
        {dimension_name}:
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: 13,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {isAll ? (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            (all)
          </Box>
        ) : (
          <>
            {visibleInstances.map((inst) => inst.name).join(', ')}
            {overflow && !expanded ? (
              <>
                {' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => setExpanded(true)}
                  sx={{
                    fontSize: 13,
                    verticalAlign: 'baseline',
                    color: 'text.secondary',
                  }}
                >
                  +{hiddenCount} more
                </Link>
              </>
            ) : null}
          </>
        )}
      </Typography>
    </Box>
  );
};
